import { FieldInput } from "./FieldInput";
import { STOCK_PCTS } from "../../lib/defaults";
import { Beaker } from "lucide-react";

export default function StepGoal({ state, set }) {
  const autoSodaAsh = Number(state.manganese_ppm) > 0.05 && Number(state.ph) < 7.5;

  return (
    <section data-testid="step-goal" className="space-y-8">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
          Treatment<br/>Goal
        </h1>
        <p className="text-white/70 text-base mt-4">
          Define target residual, stock chemistry, and optional pH adjustment.
        </p>
      </div>

      <div className="space-y-6">
        <FieldInput
          id="target_residual_ppm"
          testId="input-target-residual"
          label="Target Free Chlorine Residual"
          unit="ppm"
          value={state.target_residual_ppm}
          onChange={v => set("target_residual_ppm", v)}
          placeholder="6.0"
          step="0.1"
          help="Default 6.0 ppm — includes a strong safety margin because downstream filtration removes chlorine & byproducts before the tap. Use 0.5–1.0 ppm only when there is NO downstream carbon filter."
        />
        <FieldInput
          id="refill_days"
          testId="input-refill-days"
          label="Desired Refill Interval"
          unit="days"
          value={state.refill_days}
          onChange={v => set("refill_days", v)}
          placeholder="14"
          step="1"
          help="How often the homeowner expects to refill the solution tank."
        />
      </div>

      <div>
        <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/70 font-bold block mb-2">
          Stock Chlorine Concentration
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {STOCK_PCTS.map(p => (
            <button
              key={p}
              type="button"
              data-testid={`stock-${p}`}
              onClick={() => set("stock_chlorine_pct", p)}
              className={`h-20 border-2 flex flex-col items-center justify-center transition-colors ${
                Number(state.stock_chlorine_pct) === p
                  ? "border-[#F26522] bg-[#F26522] text-black"
                  : "border-white/15 bg-[#1A1A1A] text-white hover:border-white/40"
              }`}
            >
              <div className="font-heading text-3xl font-black">{p}%</div>
              <div className="font-mono text-[9px] tracking-widest opacity-70 mt-0.5 uppercase">
                {p === 6 ? "Household" : p === 8.25 ? "Bleach" : p === 10 ? "Standard" : "Pool Grade"}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Soda Ash co-injection */}
      <div className="bg-[#111111] border-2 border-white/15 p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F26522] flex items-center justify-center shrink-0">
              <Beaker className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading text-lg font-black uppercase text-white">Soda Ash Co-Injection</div>
              <div className="font-mono text-[10px] tracking-wider uppercase text-[#F26522]">
                pH Adjustment (Optional)
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="toggle-soda-ash"
            onClick={() => set("soda_ash_enabled", !state.soda_ash_enabled)}
            className={`w-14 h-8 border-2 shrink-0 transition-colors flex items-center px-1 ${
              state.soda_ash_enabled ? "bg-[#F26522] border-[#F26522] justify-end" : "bg-transparent border-white/30 justify-start"
            }`}
            aria-label="toggle soda ash"
          >
            <div className={`w-5 h-5 ${state.soda_ash_enabled ? "bg-black" : "bg-white/60"}`} />
          </button>
        </div>

        {autoSodaAsh && !state.soda_ash_enabled && (
          <div className="bg-black border-l-4 border-[#F26522] p-3 text-xs text-white/80">
            <span className="text-[#F26522] font-bold">AUTO-RECOMMENDED:</span> Manganese is present and pH is below 7.5.
            Soda ash will be auto-included in the spec to accelerate Mn oxidation.
          </div>
        )}

        {(state.soda_ash_enabled || autoSodaAsh) && (
          <FieldInput
            id="target_ph"
            testId="input-target-ph"
            label="Target pH (after soda ash)"
            value={state.target_ph}
            onChange={v => set("target_ph", v)}
            placeholder="8.0"
            step="0.1"
            min="6"
            max="10"
            help="8.0 = optimal for fast manganese oxidation. 8.4 = max practical. Above 8.5 reduces chlorine efficiency."
          />
        )}

        <p className="text-xs text-white/60">
          Soda ash (sodium carbonate Na₂CO₃) is injected <span className="text-[#F26522] font-bold">upstream</span> of
          chlorine to raise pH first — this lets HOCl work at full strength and dramatically speeds up Mn²⁺ oxidation.
        </p>
      </div>
    </section>
  );
}
